import * as fabric from 'fabric'
import { EditorObjectType } from '@utils/types'
import {
  CANVAS_CENTER_OFFSET,
  PRINT_AREA_SIZE,
  SPECIAL_CANVAS_OBJECT_IDS,
  TEMPORARY_CANVAS_OBJECT_IDS,
  TEXT_TYPES,
  applyCornerOnlyControls,
  scaleCanvasJsonToArea,
  translateCanvasJson,
} from './fabric-editor-helpers'
import {
  collectBaseTranslucentStyleByObjectId,
  isRenderableUserCanvasObject,
  restoreSerializedObjectFromBaseTranslucentStyle,
} from './fabric-editor-translucent-finish'

/**
 * Logiche copy/paste/sync/serializzazione estratte da `useFabricEditor`.
 */

export const serializeCanvasJson = ({
  canvas,
  objectControls,
  getBackgroundOrigin,
  getPrintAreaBoundsRelativeToBackground,
  excludeSpecialObjects = false,
}: {
  canvas: fabric.Canvas | null | undefined
  objectControls: EditorObjectType[]
  getBackgroundOrigin: () => { left: number; top: number }
  getPrintAreaBoundsRelativeToBackground: () => any
  excludeSpecialObjects?: boolean
}) => {
  if (!canvas) return null

  if (objectControls?.length) {
    const objectMap = new Map(
      objectControls.map((oc) => [oc.id, oc] as [EditorObjectType['id'], EditorObjectType])
    )
    canvas.getObjects().forEach((obj: any) => {
      if (!obj?.customId) return
      const oc = objectMap.get(obj.customId)
      if (oc) {
        obj.editorObjectControl = oc
      }
    })
  }

  const json = canvas.toJSON()
  const backgroundOrigin = getBackgroundOrigin()
  const baseTranslucentStyleByObjectId = collectBaseTranslucentStyleByObjectId(
    canvas.getObjects() as any[],
    SPECIAL_CANVAS_OBJECT_IDS
  )

  const normalizedJson = translateCanvasJson(json, -backgroundOrigin.left, -backgroundOrigin.top)
  ;(normalizedJson as any).coordinateSpace = 'background-relative'
  ;(normalizedJson as any).referenceBackground = {
    width: PRINT_AREA_SIZE,
    height: PRINT_AREA_SIZE,
  }
  ;(normalizedJson as any).referencePrintArea = getPrintAreaBoundsRelativeToBackground()

  ;(normalizedJson as any).objects = ((normalizedJson as any).objects || []).filter((obj: any) => {
    if (TEMPORARY_CANVAS_OBJECT_IDS.includes(obj?.customId)) return false
    if (excludeSpecialObjects && SPECIAL_CANVAS_OBJECT_IDS.includes(obj?.customId)) return false
    return true
  })

  ;(normalizedJson as any).objects = ((normalizedJson as any).objects || []).map((obj: any) => {
    if (!isRenderableUserCanvasObject(obj, SPECIAL_CANVAS_OBJECT_IDS)) return obj

    return restoreSerializedObjectFromBaseTranslucentStyle(
      obj,
      baseTranslucentStyleByObjectId.get(String(obj.customId))
    )
  })

  delete (json as any).backgroundImage
  delete (normalizedJson as any).backgroundImage

  return normalizedJson
}

export const loadCanvasFromJson = async ({
  canvas,
  canvasJson,
  onAreaStampaRestored,
}: {
  canvas: fabric.Canvas | null | undefined
  canvasJson: any
  onAreaStampaRestored: (instance: any) => void
}) => {
  if (!canvas) return

  const normalizedCanvasJson =
    canvasJson?.coordinateSpace === 'background-relative'
      ? translateCanvasJson(canvasJson, CANVAS_CENTER_OFFSET, CANVAS_CENTER_OFFSET)
      : canvasJson

  await canvas.loadFromJSON(normalizedCanvasJson, (jsonObj, instance) => {
    const fabricInstance = instance as any
    if (
      jsonObj.type === 'Rect' ||
      jsonObj.customId === 'area-stampa' ||
      jsonObj.customId === 'quota'
    ) {
      fabricInstance.set({
        selectable: false,
        evented: false,
      })
    }
    if (jsonObj.type === 'Rect') {
      onAreaStampaRestored(fabricInstance)
    }
  })

  canvas.getObjects().forEach((obj) => {
    if ((obj as any).customId) {
      applyCornerOnlyControls(obj)
    }
  })

  canvas.renderAll()
}

export const replaceCanvasJson = async ({
  canvas,
  canvasJson,
  getPrintAreaBoundsRelativeToBackground,
  setObjectControls,
  loghiRefs,
  preTransformStateRef,
  toggleCanvasOverlayVisibility,
  toggleCanvasObjectInteractivity,
  isPreviewVisible,
  onExportOverlayOnly,
}: {
  canvas: fabric.Canvas | null | undefined
  canvasJson: any
  getPrintAreaBoundsRelativeToBackground: () => any
  setObjectControls: (next: EditorObjectType[]) => void
  loghiRefs: { current: fabric.Object[] }
  preTransformStateRef: { current: any }
  toggleCanvasOverlayVisibility: (visible: boolean) => void
  toggleCanvasObjectInteractivity: (enabled: boolean) => void
  isPreviewVisible: boolean
  onExportOverlayOnly: () => void
}) => {
  if (!canvas) return

  const targetPrintArea = getPrintAreaBoundsRelativeToBackground()

  const serializedObjects = Array.isArray(canvasJson?.objects)
    ? canvasJson.objects.filter((obj: any) => !SPECIAL_CANVAS_OBJECT_IDS.includes(obj?.customId))
    : []

  const scaledCanvasJson = scaleCanvasJsonToArea(
    {
      ...canvasJson,
      objects: serializedObjects,
    },
    canvasJson?.referencePrintArea || null,
    targetPrintArea
  )

  const normalizedSerializedObjects =
    scaledCanvasJson?.coordinateSpace === 'background-relative'
      ? translateCanvasJson(scaledCanvasJson, CANVAS_CENTER_OFFSET, CANVAS_CENTER_OFFSET)
          ?.objects ?? []
      : scaledCanvasJson?.objects || []

  canvas.discardActiveObject()
  canvas
    .getObjects()
    .filter((obj: any) => !SPECIAL_CANVAS_OBJECT_IDS.includes(obj?.customId))
    .forEach((obj) => canvas.remove(obj))

  setObjectControls([])
  loghiRefs.current = []
  preTransformStateRef.current = null

  if (normalizedSerializedObjects.length > 0) {
    const restoredObjects = (await fabric.util.enlivenObjects(normalizedSerializedObjects)) as any[]

    restoredObjects.forEach((restoredObject) => {
      canvas.add(restoredObject)
      if ((restoredObject as any)?.customId && !TEXT_TYPES.includes(restoredObject.type)) {
        loghiRefs.current.push(restoredObject)
      }
    })
  }

  toggleCanvasOverlayVisibility(!isPreviewVisible)
  toggleCanvasObjectInteractivity(!isPreviewVisible)
  canvas.requestRenderAll()
  onExportOverlayOnly()
}
